import React from "react";
import { usd, DIVISIONS } from "./ui";
import "./Dashboard.css";

// Early-month picture: the booked forecast plus the Draft (not yet published)
// and Open (unfilled) pipeline that the other tabs exclude, with a plain-language
// notes summary for a verbal update.
function buildNotes(data) {
  const b = data.bom;
  const n = [];
  const asOf = data.reportDate || "today";
  n.push(`As of ${asOf}, ${data.monthLabel} is pacing at ${usd(b.forecast)} booked and scheduled. Only ${usd(b.completed)} has actually been worked so far; the rest is upcoming.`);
  if (b.draft > 0 || b.open > 0) {
    n.push(`${usd(b.draft)} is still in Draft and not yet published, and ${usd(b.open)} is open and unfilled, so the booked figure should climb as the schedule fills in. Total potential is ${usd(b.totalPotential)}.`);
  }
  const gem = b.byDivision.GEM.forecast, fac = b.byDivision.Facility.forecast;
  const facLoading = b.byDivision.Facility.draft > fac;
  n.push(`GEM is at ${usd(gem)} booked and Facility is at ${usd(fac)}${facLoading ? ", with a large facility Draft pipeline still to publish" : ""}.`);
  if (data.budget?.total) {
    n.push(`Budget for ${data.monthLabel} is ${usd(data.budget.total)}. Booked so far is ${Math.round((b.forecast / data.budget.total) * 100)} percent of goal, with the Draft and Open pipeline still to publish and fill.`);
  }
  const top = [...(data.clients || [])].sort((a, b) => b.co - a.co).slice(0, 3).map((c) => `${c.name} (${usd(c.co)})`).join(", ");
  if (top) n.push(`Largest clients booked so far: ${top}.`);
  return n;
}

export default function BeginningOfMonth({ data }) {
  const b = data.bom;
  if (!b) return <div className="state">No pipeline data for this month.</div>;
  const notes = buildNotes(data);

  const statusRows = [
    { label: "Completed", desc: "worked so far", value: b.completed },
    { label: "Ongoing", desc: "in progress", value: b.ongoing },
    { label: "Upcoming", desc: "scheduled, not yet worked", value: b.upcoming },
    { label: "Late", desc: "worked, flagged late", value: b.late },
    { label: "Forecast (booked + probable)", desc: "counts toward the month forecast", value: b.forecast, type: "subtotal" },
    { label: "Open", desc: "published but unfilled", value: b.open },
    { label: "Draft", desc: "not yet published", value: b.draft },
    { label: "Total potential", desc: "forecast + draft + open", value: b.totalPotential, type: "total" },
  ].filter((r) => r.value !== 0 || ["Forecast (booked + probable)", "Total potential"].includes(r.label));

  return (
    <div className="dash">
      <section className="kpis">
        <div className="kpi kpi-primary"><div className="kpi-label">Booked forecast — {data.monthLabel}</div><div className="kpi-value">{usd(b.forecast)}</div><div className="kpi-sub">{usd(b.completed)} worked so far</div></div>
        <div className="kpi"><div className="kpi-label">Draft (to publish)</div><div className="kpi-value">{usd(b.draft)}</div><div className="kpi-sub">not yet published</div></div>
        <div className="kpi"><div className="kpi-label">Open (to fill)</div><div className="kpi-value">{usd(b.open)}</div><div className="kpi-sub">unfilled shifts</div></div>
        <div className="kpi"><div className="kpi-label">Total potential</div><div className="kpi-value">{usd(b.totalPotential)}</div><div className="kpi-sub">forecast + draft + open</div></div>
      </section>

      <section className="panel">
        <div className="panel-head"><h2>Beginning of Month Notes</h2><span>{data.reportDate || ""}</span></div>
        <ul className="insights-list">
          {notes.map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      </section>

      <section className="panel">
        <div className="panel-head"><h2>Pipeline by Status</h2></div>
        <table className="grid">
          <thead><tr><th>Status</th><th>What it means</th><th>Revenue</th></tr></thead>
          <tbody>
            {statusRows.map((r) => (
              <tr key={r.label} className={r.type === "total" ? "total-row" : r.type === "subtotal" ? "subhead-row" : ""}>
                <td className="rowhead">{r.label}</td>
                <td className="muted">{r.desc}</td>
                <td className="strong">{usd(r.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="panel">
        <div className="panel-head"><h2>Pipeline by Division</h2></div>
        <table className="grid">
          <thead><tr><th>Division</th><th>Booked</th><th>Draft</th><th>Open</th><th>Potential</th></tr></thead>
          <tbody>
            {DIVISIONS.map((div) => {
              const d = b.byDivision[div] || { forecast: 0, draft: 0, open: 0 };
              return (
                <tr key={div}>
                  <td className="rowhead"><span className={"pill pill-" + div.toLowerCase()}>{div}</span></td>
                  <td>{usd(d.forecast)}</td>
                  <td>{usd(d.draft)}</td>
                  <td>{usd(d.open)}</td>
                  <td className="strong">{usd(d.forecast + d.draft + d.open)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}
